import { IClientOrder } from "interfaces/IClientOrders";
import { PaymentMethodsMap } from "interfaces/IPaymentMethod";
import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import ReviewModal from "./ReviewModal";
import { getMyReview, createAuthenticatedAxiosInstance } from "api/auth";
import { useAuthStore } from "../../../../../zustand/loginStore";
import { getOrderProductImage } from "../../../../../utils/imagesAndColors";

/**
 * Custom props for the client order details
 */
interface Props {
  /**
   * The order details
   */
  order: IClientOrder;
  /**
   * function that handles the close details button
   */
  onCloseDetails: () => void;
  /**
   * Whether the component is in mobile view
   */
  isMobile?: boolean;
}

/**
 * Formats a date string to Spanish format (e.g., "31 de noviembre del 2024")
 * @param dateString - The date string to format
 * @returns Formatted date string in Spanish
 */
const formatDateToSpanish = (dateString: string): string => {
  try {
    // Create date object from the string
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid
    }

    // Spanish month names
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} de ${month} del ${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original if error occurs
  }
};

/**
 * The component of an client's order in a detailed view
 */
export const ClientOrderDetails = ({ order, onCloseDetails, isMobile = false }: Props) => {
  const {
    id,
    status,
    order_date,
    shipping_address,
    payment_method,
    order_products,
    total,
    estimated_delivery,
    tracking_number,
    tracking_name,
  } = order;
  const subtotal = order_products.reduce((prev, order) => {
    return prev + Number(order.subtotal);
  }, 0);

  // Format the dates
  const formattedOrderDate = formatDateToSpanish(order_date);
  const formattedEstimatedDelivery = formatDateToSpanish(estimated_delivery);

  // Check if order is not pending to show shipping details
  const showShippingDetails = status.toLowerCase() !== "pending";

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [allProductsReviewed, setAllProductsReviewed] = useState(false);
  const [checkingReviews, setCheckingReviews] = useState(false);
  const { token } = useAuthStore();

  // Verificar si todos los productos ya están calificados
  useEffect(() => {
    if (status === "Entregado" && order_products.length > 0) {
      setCheckingReviews(true);
      const axiosInstance = createAuthenticatedAxiosInstance(token);

      const checkAllReviews = async () => {
        try {
          const reviewPromises = order_products.map((product) =>
            getMyReview(axiosInstance, product.product_id)
          );

          const reviews = await Promise.all(reviewPromises);
          const allReviewed = reviews.every((review) => review !== null);
          setAllProductsReviewed(allReviewed);
        } catch (error) {
          console.error("Error checking reviews:", error);
          setAllProductsReviewed(false);
        } finally {
          setCheckingReviews(false);
        }
      };

      checkAllReviews();
    }
  }, [order_products, status, token]);

  if (isMobile) {
    return (
      <div className="mobile-order-details">
        {/* Header: Número de pedido y status */}
        <div className="mobile-order-header">
          <div className="mobile-order-number">
            <strong>Pedido #{id}</strong>
          </div>
          <div className="mobile-order-status">
            <strong>{status}</strong>
          </div>
        </div>

        {/* Información del pedido */}
        <div className="mobile-order-info">
          <div className="mobile-info-item">
            <div className="mobile-info-label">Pedido realizado:</div>
            <div className="mobile-info-value">{formattedOrderDate}</div>
          </div>

          {showShippingDetails && (
            <div className="mobile-info-item">
              <div className="mobile-info-label">Fecha estimada de entrega:</div>
              <div className="mobile-info-value">{formattedEstimatedDelivery}</div>
            </div>
          )}

          <div className="mobile-info-item">
            <div className="mobile-info-label">Dirección de entrega:</div>
            <div className="mobile-info-value">{shipping_address}</div>
          </div>

          {showShippingDetails && tracking_name && (
            <div className="mobile-info-item">
              <div className="mobile-info-label">Empresa de transporte:</div>
              <div className="mobile-info-value">{tracking_name}</div>
            </div>
          )}

          {showShippingDetails && tracking_number && (
            <div className="mobile-info-item">
              <div className="mobile-info-label">Número de rastreo:</div>
              <div className="mobile-info-value mobile-tracking">{tracking_number}</div>
            </div>
          )}
        </div>

        {/* Productos */}
        <div className="mobile-products-section">
          <div className="mobile-info-label">Productos:</div>
          {order_products.map((product, index) => (
            <div key={product.product_id} className="mobile-product-item">
              <div className="mobile-product-content">
                <img 
                  src={getOrderProductImage(product)} 
                  className="mobile-product-image" 
                  alt={product.product_name}
                />
                <div className="mobile-product-details">
                  <div className="mobile-product-title">{product.product_name}</div>
                  <div className="mobile-product-spec">Talla: {product.size}</div>
                  <div className="mobile-product-spec">Color: {product.colorName}</div>
                </div>
              </div>
              <div className="mobile-product-bottom">
                <div className="mobile-product-quantity">Cantidad: {product.quantity}</div>
                <div className="mobile-product-total">${product.subtotal}</div>
              </div>
              {index < order_products.length - 1 && <div className="mobile-product-separator" />}
            </div>
          ))}
        </div>

        {/* Totales */}
        <div className="mobile-totals">
          <div className="mobile-total-item">
            <span>Subtotal:</span>
            <span>${subtotal}</span>
          </div>
          <div className="mobile-total-item">
            <span>Costo de envío:</span>
            <span>${total - subtotal}</span>
          </div>
          <div className="mobile-total-separator" />
          <div className="mobile-total-final">
            <span>Total de compra:</span>
            <span>${total}</span>
          </div>
        </div>

        {/* Botones */}
        <div className="mobile-order-actions">
          <button className="mobile-close-btn" onClick={onCloseDetails}>
            Cerrar
          </button>
          {status === "Entregado" && !allProductsReviewed && !checkingReviews && (
            <button
              className="mobile-review-btn"
              onClick={() => setShowReviewModal(true)}
            >
              Calificar
            </button>
          )}
          {status === "Entregado" && allProductsReviewed && (
            <span className="mobile-reviewed-text">
              Compra calificada
            </span>
          )}
        </div>

        {/* Modal de calificación */}
        {showReviewModal && (
          <ReviewModal
            orderProducts={order_products}
            userId={order.user_id}
            onClose={() => setShowReviewModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <Col className="client-order-details my-4">
      <div className="d-flex justify-content-between mb-2">
        <div className="d-flex gap-2">
          <h5 className="client-order-link">
            <strong>Pedido #{id}</strong>
          </h5>
        </div>
        <h5>
          <strong>{status}</strong>
        </h5>
      </div>
      <div className="d-flex gap-2">
        <h5>
          <strong>Pedido realizado:</strong>
        </h5>{" "}
        <h5>{formattedOrderDate}</h5>
      </div>

      {/* Solo mostrar fecha estimada de entrega si no está pending */}
      {showShippingDetails && (
        <div className="d-flex gap-2">
          <h5>
            <strong>Fecha estimada de entrega:</strong>
          </h5>{" "}
          <h5>{formattedEstimatedDelivery}</h5>
        </div>
      )}

      <Row className="mb-3">
        <Col>
          <h5>
            <strong>Dirección de entrega:</strong>
          </h5>{" "}
          <h5>{shipping_address}</h5>
        </Col>
      </Row>

      {/* Solo mostrar detalles de envío si no está pending */}
      {showShippingDetails && (
        <Row className="mb-3">
          {tracking_name && (
            <Col>
              <div className="d-flex gap-2">
                <h5>
                  <strong>Empresa de transporte:</strong>
                </h5>{" "}
                <h5>{tracking_name}</h5>
              </div>
            </Col>
          )}
          {tracking_number && (
            <Col>
              <div className="d-flex gap-2">
                <h5>
                  <strong>Numero de rastreo:</strong>
                </h5>{" "}
                <h5 style={{ textDecoration: "underline" }}>
                  {tracking_number}
                </h5>
              </div>
            </Col>
          )}
        </Row>
      )}

      <Col className="d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between mb-1">
          <h5>
            <strong>Productos</strong>
          </h5>
          <h5>
            <strong>Total de productos: {order_products.length}</strong>
          </h5>
        </div>
        <Col>
          {order_products.map((order) => (
            <Row key={order.product_id}>
              <img src={getOrderProductImage(order)} className="client-order-image" />
              <Col>
                <h4>{order.product_name}</h4>
                <div className="d-flex gap-2">
                  <h5>
                    <strong>Talla: {order.size}</strong>
                  </h5>
                </div>
                <div className="d-flex gap-2">
                  <h5>
                    <strong>Color: {order.colorName}</strong>
                  </h5>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="d-flex gap-2">
                    <h5>
                      <strong>Cantidad: {order.quantity}</strong>
                    </h5>
                  </div>
                  <div className="d-flex gap-2">
                    <h5>
                      <strong>Total de producto: </strong>
                    </h5>{" "}
                    <h4 className="client-order-price">${order.subtotal}</h4>
                  </div>
                </div>
              </Col>
              <div className="order-product-separator" />
            </Row>
          ))}
        </Col>
        <Col className="my-4">
          <div className="d-flex gap-2 justify-content-end">
            <h5>
              <strong>Subtotal: </strong>
            </h5>
            <h4 className="client-order-price">${subtotal}</h4>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <h5>
              <strong>Costo de envío: </strong>
            </h5>
            <h4 className="client-order-price">${total - subtotal}</h4>
          </div>
          <div className="d-flex justify-content-end">
            <div
              className="order-product-separator m-0 my-2"
              style={{ width: "30%" }}
            />
          </div>
          <div className="d-flex justify-content-between">
            <button className="btn btn-secondary" onClick={onCloseDetails}>
              Cerrar
            </button>
            {status === "Entregado" &&
              !allProductsReviewed &&
              !checkingReviews && (
                <button
                  className="btn btn-calificar"
                  style={{
                    background: "#997404",
                    color: "white",
                    borderRadius: "0.375rem",
                    marginRight: "38rem",
                  }}
                  onClick={() => setShowReviewModal(true)}
                >
                  Calificar
                </button>
              )}
            {status === "Entregado" && allProductsReviewed && (
              <span
                style={{
                  color: "#28a745",
                  fontWeight: "bold",
                  marginLeft: 8,
                  alignSelf: "center",
                }}
              >
                Compra calificada
              </span>
            )}
            <div className="d-flex gap-2">
              <h5>
                <strong>Total de compra: </strong>
              </h5>{" "}
              <h5 className="client-order-price">${total}</h5>
            </div>
          </div>
        </Col>
      </Col>
      {/* Modal de calificación */}
      {showReviewModal && (
        <ReviewModal
          orderProducts={order_products}
          userId={order.user_id}
          onClose={() => setShowReviewModal(false)}
        />
      )}
    </Col>
  );
};
